import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import COLORS from "../colors";
import {
  deepFreeze,
  formatCurrency,
  formatPercentage,
  roundPrice,
  timestampToDate,
} from "../util/misc";
import { cloneDeep, compact, every, forEach, isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const COMMISSION_FIELDS = {
  // What this record earns, read two ways (see `isSplitCommission`):
  //   share      -- this person's share of the sale's commission pot; the
  //                 shares across `sale.assignedTo` total 100.
  //   fixed rate -- `isSplitCommission: true`: a percent of the sale's
  //                 commissionable pre-tax paid off the top, whatever the
  //                 margin. Deducted from the share-holders via `splitsGiven`.
  split: 100,
  teamID: null, // Sales team ID if this is a team sale
  teamMemberCount: null, // Number of team members at time of sale (for sliding scale division)
  soldDate: null,
  completedDate: null,
  expectedPreTax: null,
  expectedCost: null,
  actualPreTax: null,
  actualCost: null,
  payApps: {},
  // Approval / payment tracking surfaces used by the held-pay flow.
  // `totalApproved`/`totalPaid` are nullable scalars; `payments` is the
  // sorted ledger surfaced to the UI via the `payments` getter.
  totalApproved: null,
  totalPaid: null,
  payments: [],

  // Fixed-rate splits carved out of this sale's commission pot, as
  // [{ userID, percent }] where percent is of the commissionable pre-tax.
  // Every record on the sale carries the same list: share-holders fund it in
  // proportion to their share (`shareRate`); a recipient scales by it when
  // the available commission cannot cover every split (`fixedRate`).
  splitsGiven: null,

  // Fixed-rate split recipient: `split` is a percent of the sale's pre-tax,
  // not a share of the pot. The recipient is an ordinary `sale.assignedTo`
  // entry, so touchSale/touchInvoice maintain this record like any other.
  isSplitCommission: false,
  splitFromUserID: null, // The salesperson whose commission funds this split
  // LEGACY ONLY. Records created before 2026-09 lived at
  // `{projectID+saleID}-split`, outside `sale.assignedTo`, and are found by
  // this field (touchInvoice / cancelSale collection-group queries). New
  // records never set it: being an assignee record, setting it would count
  // each invoice twice. See scripts/scan-commission-splits.mjs.
  splitFromSaleID: null,

  // ADVANCED COMMISSION PAYMENTS — commission paid out ahead of the sale
  // being finished, deducted from the final commission at completion.
  //
  // Keyed by advance id. Each entry:
  //   { amount, date, type, note, paycheck, paid }
  // `amount` is snapshotted when the advance is approved onto a paycheck, so
  // a later edit to the quote cannot move money that has already been paid.
  // `type` records what justified it — see ADVANCE_TYPES: "paidInFull" for
  // the automatic 50% on a sale paid up front (stored under the fixed key
  // `paidInFull`, one per sale), or "manual" for an advance payroll granted
  // by hand against upcoming commission (stored under the id of the paycheck
  // that paid it, so a sale can take several over time). Both net out of the
  // completion true-up through `remainingCommission`.
  advances: {},

  // Set server-side when the collected down payment covers the full signed
  // order total. Presence means an advance may be claimed; it does not grant
  // one. Cleared if the sale stops qualifying.
  paidInFullDate: null,

  // Status
  paycheck: null,
  paid: false,
  locked: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(COMMISSION_FIELDS);

export const COMMISSION_STATUS_TYPES = {
  expected: {
    order: 1,
    label: "Expected",
    icon: "pending",
    color: "warning",
  },
  partiallyReady: {
    order: 2,
    label: "Partially ready for approval",
    icon: "undone",
    color: "warning",
  },
  unapproved: {
    order: 2,
    label: "Ready for approval",
    icon: "undone",
    color: "warning",
  },
  approved: {
    order: 3,
    label: "Approved",
    icon: "done",
    color: "positive",
  },
  paid: {
    order: 4,
    label: "Paid",
    icon: "paid",
    color: "positive",
  },
  noCommission: {
    order: 5,
    label: "No commission due",
    icon: "closed",
    color: "info",
  },
};
deepFreeze(COMMISSION_STATUS_TYPES);

// The automatic paid-up-front claim. Also the fixed key it is stored under in
// `advances`, which is why touchPaycheck derives the commission-doc key from
// the type.
export const ADVANCE_TYPE_PAID_IN_FULL = "paidInFull";
// An advance payroll granted by hand against a sale that is not finished.
// Stored under the paying paycheck's id.
export const ADVANCE_TYPE_MANUAL = "manual";

// What justified an advance. Labels are i18n keys (sentence case).
export const ADVANCE_TYPES = {
  paidInFull: {
    label: "Paid in full up front",
    icon: "paid",
  },
  manual: {
    label: "Commission advance",
    icon: "awaiting_payment",
  },
};
deepFreeze(ADVANCE_TYPES);

// Commission statuses on which payroll may grant a manual advance: the sale
// is not finished (or its actual figures are not in yet), so nothing is
// payable on it and the advance is against commission still to come.
// `unapproved`/`approved` are excluded on purpose — the real commission is
// claimable there, so pay that instead of advancing against it.
export const ADVANCEABLE_STATUSES = ["expected", "partiallyReady"];
deepFreeze(ADVANCEABLE_STATUSES);

// Statuses in which the whole commission can be (or has been) paid, so the
// paid-up-front advance no longer applies.
export const WHOLE_PAYABLE_STATUSES = ["unapproved", "approved", "paid"];
deepFreeze(WHOLE_PAYABLE_STATUSES);

// Default sliding scale brackets - used when employee has no custom brackets
export const DEFAULT_COMMISSION_BRACKETS = [
  { maxMargin: 20.5, commission: 0 },
  { maxMargin: 22.5, commission: 1 },
  { maxMargin: 24.5, commission: 2 },
  { maxMargin: 26.5, commission: 4 },
  { maxMargin: 28.5, commission: 6 },
  { maxMargin: 30.5, commission: 8 },
  { maxMargin: 32.5, commission: 10 },
  { maxMargin: 34.5, commission: 12 },
  { maxMargin: 36.5, commission: 14 },
  { maxMargin: 39.5, commission: 16 },
  { maxMargin: 44.5, commission: 19 },
  { maxMargin: 48.5, commission: 24 },
  { maxMargin: 54.5, commission: 26 },
  { maxMargin: 64.5, commission: 30 },
  { maxMargin: 74.5, commission: 35 },
  { maxMargin: Infinity, commission: 40 },
];
deepFreeze(DEFAULT_COMMISSION_BRACKETS);

export const commissionPercent = (marginPercent, brackets = null) => {
  const scale = brackets || DEFAULT_COMMISSION_BRACKETS;
  for (const bracket of scale) {
    if (marginPercent < bracket.maxMargin) {
      return bracket.commission;
    }
  }
  // Return the last bracket's commission if margin exceeds all brackets
  return scale[scale.length - 1].commission;
};

export default class Commission extends DataStructure {
  constructor(userID, id, data = {}) {
    data = sanitize(data, COMMISSION_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.completedDate = timestampToDate(data.completedDate);
    data.paidInFullDate = timestampToDate(data.paidInFullDate);
    Object.values(data.payApps).forEach((payApp) => {
      payApp.date = timestampToDate(payApp.date);
    });
    Object.values(data.advances).forEach((advance) => {
      advance.date = timestampToDate(advance.date);
    });

    super(data);

    this.id = id || null;
    this.userID = userID;
    this.permissions = Object.freeze({
      view: Commission.canBeViewedBy,
      edit: this.canBeEditedBy,
      unlock: this.canBeUnlockedBy,
    });
  }

  get projectID() {
    return this.id.slice(0, -1);
  }

  get saleID() {
    return this.id.slice(-1);
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isAdmin || (user.isEmployee && user.hasRole("payroll"));
  }

  canBeEditedBy(user) {
    return false;
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return Commission.getPath(this.userID, this.id);
  }

  get stateID() {
    return this.id + this.userID;
  }

  static getStateID(path) {
    const match = path.match(/^users\/([^/]+)\/commission\/([^/]+)$/);
    if (match) {
      return match[2] + match[1];
    }
    return null;
  }

  static getPath(userID, id) {
    let path = `users/${userID}/commission`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(commission) {
    return commission.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Commission(snapshot.ref.parent.parent.id, snapshot.id, data);
  }

  // Editing

  get rules() {
    return {
      totalApproved: [
        (a) => !a || a <= this.actualMargin || "approvedExceedsMargin",
      ],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(this.data[key]) === true)
    );
  }

  get isEmpty() {
    return !this.id && isEmpty(diff(this.data, COMMISSION_FIELDS));
  }

  isEqual(commission) {
    return (
      this.id === commission.id &&
      isEmpty(diff(cloneDeep(this.data), cloneDeep(commission.data)))
    );
  }

  // Formatting

  get project() {
    return this.projectID ? store.state.main.projects[this.projectID] : null;
  }

  get sale() {
    return this.saleID ? store.state.main.sales[this.id] : null;
  }

  get split() {
    return this.data.split / 100;
  }

  get splitPercentFormatted() {
    return formatPercentage(this.data.split);
  }

  get expectedMarginPercent() {
    if (this.data.expectedPreTax === null || this.data.expectedCost === null) {
      return null;
    }
    if (this.data.expectedPreTax === 0) {
      return 0;
    }
    return (
      (100 * (this.data.expectedPreTax - this.data.expectedCost)) /
      this.data.expectedPreTax
    );
  }

  get expectedMargin() {
    if (this.data.expectedPreTax === null || this.data.expectedCost === null) {
      return null;
    }
    return this.data.expectedPreTax - this.data.expectedCost;
  }

  get isFlatRate() {
    const schedule = this.getCommissionScheduleForDate(this.data.soldDate);
    return schedule && schedule.type === "flat";
  }

  // The rate this record earns on the commissionable pre-tax, given the pot
  // rate the margin lands on (sliding scale) or the flat rate.
  //
  // Fixed-rate splits given (`splitsGiven`) come off the top of the pot, so
  // every share-holder funds them in proportion to their share; what is left
  // is divided by share. Justin (2026-09-09): a 2% split on a sale paying 6%
  // leaves the salesperson 4%, not 98% of 6%.
  //
  // `teamID` set means this share came from the Sales Team mechanism
  // (getCommissionRecipients dividing a team sale evenly) -- each member
  // still earns their own full flat rate there, unreduced by that
  // bookkeeping share, though they still fund their part of a split given.
  // Without a team, a share is an individually-entered override (e.g. a
  // helper's cut) and IS the rate.
  shareRate(potPercent) {
    // The splits come out of what the sale earns; they can take all of it
    // but never more, so a share is never pushed below zero.
    const given = Math.min(this.totalSplitsGivenPercent, potPercent);
    if (this.isFlatRate && this.data.teamID) {
      return potPercent - given * this.split;
    }
    return (potPercent - given) * this.split;
  }

  // The salesperson whose commission a fixed-rate split comes out of. On a
  // share-holder's own record that is themselves.
  get funder() {
    if (!this.isSplitCommission) return this.user;
    return this.splitFromUser || null;
  }

  // What a fixed-rate split pays on this sale, as a percent of the pre-tax.
  // Its rate, unless the available commission -- what the sale's margin
  // earns the salesperson funding it -- cannot cover the sale's fixed-rate
  // splits; then they all scale down together, to nothing on a sale that
  // earns no commission. Justin (2026-09-10): "If negative or below
  // commission rates, the split of the available commission would be 0
  // also." A legacy record carries no cost figures and no split list, so
  // its rate stands as entered until it is migrated.
  fixedRate(marginPercent) {
    const rate = this.data.split;
    if (marginPercent === null) return rate;
    const available = this.getCommissionPercent(marginPercent, this.funder);
    const splits = this.totalSplitsGivenPercent || rate;
    return splits > 0 ? rate * Math.min(1, available / splits) : 0;
  }

  get expectedCommissionPercent() {
    // A fixed-rate split: its rate, capped by what the sale earns.
    if (this.isSplitCommission) {
      return this.fixedRate(this.expectedMarginPercent);
    }
    const percent = this.expectedMarginPercent;
    if (percent === null) return null;
    return this.shareRate(this.getCommissionPercent(percent));
  }

  get expectedCommission() {
    const percent = this.expectedCommissionPercent;
    return percent !== null ? (this.data.expectedPreTax * percent) / 100 : null;
  }

  get actualMarginPercent() {
    if (this.actualPreTax === null || this.actualCost === null) {
      return null;
    }
    if (this.actualPreTax === 0) {
      return 0;
    }
    return (100 * (this.actualPreTax - this.actualCost)) / this.actualPreTax;
  }

  get actualMargin() {
    if (this.actualPreTax === null || this.actualCost === null) {
      return null;
    }
    return this.actualPreTax - this.actualCost;
  }

  get actualCommissionPercent() {
    if (this.isSplitCommission) {
      // No actual figure until the pre-tax is invoiced, like every record.
      if (this.actualPreTax === null) return null;
      const margin = this.actualMarginPercent;
      // A legacy record never receives actualCost; its rate stands.
      if (margin === null) {
        return this.isLegacySplitRecord ? this.data.split : null;
      }
      return this.fixedRate(margin);
    }
    const percent = this.actualMarginPercent;
    if (percent === null) return null;
    return this.shareRate(this.getCommissionPercent(percent));
  }

  getCommissionScheduleForDate(saleDate, user = this.user) {
    if (!user) {
      return null;
    }

    // Helper to convert to timestamp for comparison
    const toTimestamp = (date) => {
      if (!date) return null;
      if (date instanceof Date) return date.getTime();
      if (date.toDate) return date.toDate().getTime(); // Firestore Timestamp
      if (typeof date === "string" || typeof date === "number")
        return new Date(date).getTime();
      return null;
    };

    const saleDateTs = toTimestamp(saleDate);
    const effectiveDateTs = toTimestamp(user.data.commissionEffectiveDate);

    // Check if sale date is before current schedule's effective date
    if (effectiveDateTs && saleDateTs && saleDateTs < effectiveDateTs) {
      // Look through history for the applicable schedule
      const history = user.data.commissionScheduleHistory || [];
      // History is sorted by effectiveDate descending (most recent first)
      for (const schedule of history) {
        const scheduleEffectiveTs = toTimestamp(schedule.effectiveDate);
        if (scheduleEffectiveTs && saleDateTs >= scheduleEffectiveTs) {
          return schedule;
        }
      }
      // If no matching history, use the oldest schedule or default
      return history.length > 0 ? history[history.length - 1] : null;
    }

    // Use current schedule
    return {
      type: user.data.commissionType,
      flatRate: user.data.commissionFlatRate,
      minMargin: user.data.commissionMinMargin,
      brackets: user.data.commissionBrackets,
    };
  }

  // The commission rate `user`'s schedule earns at this margin on this sale.
  // Defaults to the record's owner; a fixed-rate split is measured against
  // the schedule of the salesperson funding it (see `funder`).
  getCommissionPercent(marginPercent, user = this.user) {
    if (!user) {
      return commissionPercent(marginPercent);
    }

    // Get the appropriate schedule based on sale date
    const schedule = this.getCommissionScheduleForDate(
      this.data.soldDate,
      user
    );
    if (!schedule) {
      return commissionPercent(marginPercent);
    }

    switch (schedule.type) {
      case "none":
        return 0;
      case "flat":
        // Flat rate: each team member earns their full flat rate on all team sales
        return marginPercent >= (schedule.minMargin || 0)
          ? schedule.flatRate || 0
          : 0;
      case "sliding":
      default:
        // Sliding scale: team split is already applied via this.split multiplier
        return commissionPercent(marginPercent, schedule.brackets);
    }
  }

  get actualCommission() {
    const percent = this.actualCommissionPercent;
    return percent !== null ? (this.actualPreTax * percent) / 100 : null;
  }

  get totalHeld() {
    // null/undefined both yield null — guards against the case where the
    // doc predates the totalApproved field (sanitize won't backfill).
    if (this.data.totalApproved == null) return null;
    return this.actualCommission - this.data.totalApproved;
  }

  get totalHeldFormatted() {
    const held = this.totalHeld;
    return held !== null ? formatCurrency(held) : null;
  }

  get totalApprovedFormatted() {
    return this.data.totalApproved !== null
      ? formatCurrency(this.data.totalApproved)
      : null;
  }

  get totalPaidFormatted() {
    return this.data.totalPaid !== null
      ? formatCurrency(this.data.totalPaid)
      : null;
  }

  // Chronological view of the payment ledger (oldest first) with
  // amounts pre-formatted as currency strings, suitable for direct
  // rendering in the paycheck-history UI.
  get payments() {
    const raw = Array.isArray(this.data.payments) ? this.data.payments : [];
    return raw
      .slice()
      .sort((a, b) => {
        const ad = new Date(a?.date || 0).getTime();
        const bd = new Date(b?.date || 0).getTime();
        return ad - bd;
      })
      .map((p) => ({
        ...p,
        amount: formatCurrency(p?.amount ?? 0),
      }));
  }

  get expectedPreTaxFormatted() {
    return this.data.expectedPreTax !== null
      ? formatCurrency(this.data.expectedPreTax)
      : null;
  }

  get expectedCostFormatted() {
    return this.data.expectedCost !== null
      ? formatCurrency(this.data.expectedCost)
      : null;
  }

  get expectedMarginPercentFormatted() {
    const percent = this.expectedMarginPercent;
    return percent !== null ? formatPercentage(percent) : null;
  }

  get expectedMarginFormatted() {
    return this.expectedMargin !== null
      ? formatCurrency(this.expectedMargin)
      : null;
  }

  get expectedCommissionPercentFormatted() {
    const percent = this.expectedCommissionPercent;
    return percent !== null ? formatPercentage(percent) : null;
  }

  get expectedCommissionFormatted() {
    const commission = this.expectedCommission;
    return commission !== null ? formatCurrency(commission) : null;
  }

  get actualPreTaxFormatted() {
    return this.actualPreTax !== null
      ? formatCurrency(this.actualPreTax)
      : null;
  }

  get actualPreTaxDiff() {
    if (this.actualPreTax === null) {
      return null;
    }
    return this.actualPreTax - this.data.expectedPreTax;
  }

  get actualPreTaxDiffFormatted() {
    const difference = this.actualPreTaxDiff;
    if (difference === null) {
      return null;
    }
    return (difference >= 0 ? "+" : "") + formatCurrency(difference);
  }

  get actualCostFormatted() {
    return this.actualCost !== null ? formatCurrency(this.actualCost) : null;
  }

  get actualCostDiff() {
    if (this.actualCost === null) {
      return null;
    }
    return this.actualCost - this.data.expectedCost;
  }

  get actualCostDiffFormatted() {
    const difference = this.actualCostDiff;
    if (difference === null) {
      return null;
    }
    return (difference >= 0 ? "+" : "") + formatCurrency(difference);
  }

  get actualMarginPercentFormatted() {
    const percent = this.actualMarginPercent;
    return percent !== null ? formatPercentage(percent) : null;
  }

  get actualMarginFormatted() {
    return this.actualMargin !== null
      ? formatCurrency(this.actualMargin)
      : null;
  }

  get actualMarginDiff() {
    if (this.actualMargin === null) {
      return null;
    }
    return this.actualMargin - this.expectedMargin;
  }

  get actualMarginDiffFormatted() {
    const difference = this.actualMarginDiff;
    if (difference === null) {
      return null;
    }
    return (difference >= 0 ? "+" : "") + formatCurrency(difference);
  }

  get actualCommissionPercentFormatted() {
    const percent = this.actualCommissionPercent;
    return percent !== null ? formatPercentage(percent) : null;
  }

  get actualCommissionFormatted() {
    const commission = this.actualCommission;
    return commission !== null ? formatCurrency(commission) : null;
  }

  get actualCommissionDiff() {
    if (this.actualCommission === null) {
      return null;
    }
    return this.actualCommission - this.expectedCommission;
  }

  get actualCommissionDiffFormatted() {
    const difference = this.actualCommissionDiff;
    if (difference === null) {
      return null;
    }
    return (difference >= 0 ? "+" : "") + formatCurrency(difference);
  }

  get hasPayApps() {
    return Object.keys(this.data.payApps).length > 0;
  }

  get payApps() {
    return Object.keys(this.data.payApps).map(
      (id) => store.state.main.invoices[id]
    );
  }

  get payAppCommission() {
    const c = {};
    const percent = this.expectedCommissionPercent;
    forEach(
      this.data.payApps,
      (pa, id) => (c[id] = (pa.pretax * percent) / 100)
    );
    return c;
  }

  // `!id in this.data.payApps` parsed as `(!id) in ...`, which is always
  // false — so the guard never fired and an unknown id fell through to
  // `this.data.payApps[id].pretax` and threw. Every caller passes a key it
  // just read off the map, which is why it never surfaced.
  getPayAppMargin(id) {
    const percent = this.expectedMarginPercent;
    if (!(id in this.data.payApps)) {
      return null;
    }
    return (this.data.payApps[id].pretax * percent) / 100;
  }

  getPayAppCommission(id) {
    const percent = this.expectedCommissionPercent;
    if (!(id in this.data.payApps)) {
      return null;
    }
    return (this.data.payApps[id].pretax * percent) / 100;
  }

  getPayAppStatus(id) {
    const payApp = this.data.payApps[id];
    if (!payApp.paycheck) {
      return "unapproved";
    } else if (!payApp.paid) {
      return "approved";
    } else {
      return "paid";
    }
  }

  get payAppStatuses() {
    const c = {};
    Object.keys(this.data.payApps).forEach(
      (id) => (c[id] = this.getPayAppStatus(id))
    );
    return c;
  }

  getPayAppStatusType(id) {
    const type = COMMISSION_STATUS_TYPES[this.getPayAppStatus(id)];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get payAppStatusTypes() {
    const c = {};
    Object.keys(this.data.payApps).forEach(
      (id) => (c[id] = this.getPayAppStatusType(id))
    );
    return c;
  }

  get paycheck() {
    return this.data.paycheck
      ? store.state.main.paychecks[this.data.paycheck]
      : null;
  }

  get paycheckIDs() {
    return compact([
      this.data.paycheck,
      ...Object.values(this.data.payApps).map((pa) => pa.paycheck),
      // Advances are paid on a check too, and without this the advance path
      // could not reach its own paycheck -- so `paychecks`, and the paid date
      // derived from it, were blind to a sale paid in full up front.
      ...Object.values(this.data.advances || {}).map((a) => a.paycheck),
    ]);
  }

  get paychecks() {
    return compact(
      this.paycheckIDs.map((id) => store.state.main.paychecks[id])
    );
  }

  /**
   * Has commission money actually moved for this sale?
   *
   * NOT the same as `data.paid`. `touchPaycheck` only sets `paid` for a
   * whole-commission entry (functions/paychecks.js): a pay-app payment flips
   * `payApps.{id}.paid`, and an advance flips `advances.{id}.paid` while
   * deliberately leaving the commission open for its true-up. So `data.paid`
   * reads false on AIA sales and on sales paid in full up front, even though
   * the salesperson has been paid.
   *
   * Craig's clawback rule branches on exactly this: a critical issue billed to
   * job cost "after commission was already paid for that sale... if not
   * already paid, it's just an additional sale expense". Reading `data.paid`
   * there would bill the company 100% on precisely the sales where money
   * demonstrably already moved.
   */
  get hasBeenPaid() {
    if (this.data.paid) return true;
    if (Object.values(this.data.payApps).some((pa) => pa.paid)) return true;
    return Object.values(this.data.advances || {}).some((a) => a.paid);
  }

  /**
   * When commission money FIRST moved for this sale, or null if it has not.
   *
   * First rather than last: Craig's branch turns on the moment a sale stops
   * being "not already paid", and on a staged AIA sale that is the first pay
   * app, not the final true-up. Flagged as an assumption -- a sale paid across
   * several checks has no single payment date, so if the intent is instead to
   * compare against the LAST payment this getter is the one place to change.
   */
  get paidDate() {
    // `paidDate` is null on a pending check (it needs payrollDate or a
    // payment transaction), so compact() is the paid filter.
    const dates = compact(this.paychecks.map((p) => p.paidDate))
      .map((d) => timestampToDate(d))
      .filter((d) => d instanceof Date && !isNaN(d.getTime()));
    if (!dates.length) return null;
    return new Date(Math.min(...dates.map((d) => d.getTime())));
  }

  get status() {
    if (Object.values(this.payAppStatuses).includes("unapproved")) {
      return "partiallyReady";
    } else if (
      !this.data.completedDate ||
      this.actualPreTax === null ||
      this.actualCost === null
    ) {
      return "expected";
    } else if (this.actualPreTax <= 0) {
      return "noCommission";
    } else if (!this.data.paycheck) {
      return "unapproved";
    } else if (!this.data.paid) {
      return "approved";
    } else {
      return "paid";
    }
  }

  get statusType() {
    const type = COMMISSION_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get isClosed() {
    return this.data.paid === true || this.status === "noCommission";
  }

  get user() {
    return store.state.main.users[this.userID];
  }

  // Split commission getters

  // True when `split` is a fixed percent of the sale's pre-tax paid off the
  // top (a split received), rather than a share of the commission pot.
  get isSplitCommission() {
    return Boolean(this.data.isSplitCommission);
  }

  // A LEGACY split record: one created at `{projectID+saleID}-split`, outside
  // `sale.assignedTo`. Nothing maintains its completion or cost figures, so it
  // cannot reach a payable status until migrated (scan-commission-splits.mjs).
  get isLegacySplitRecord() {
    return this.isSplitCommission && Boolean(this.data.splitFromSaleID);
  }

  get splitFromUser() {
    return this.data.splitFromUserID
      ? store.state.main.users[this.data.splitFromUserID]
      : null;
  }

  // Percent of the pre-tax carved out of this sale's pot for fixed-rate
  // splits. Zero on a split recipient's own record.
  get totalSplitsGivenPercent() {
    if (!this.data.splitsGiven || this.data.splitsGiven.length === 0) {
      return 0;
    }
    return this.data.splitsGiven.reduce((sum, s) => sum + (s.percent || 0), 0);
  }

  // Everything this record earns on the sale, at the ACTUAL rate. Splits
  // given are already carved out by `shareRate`, so this is the actual
  // commission; the name is kept because payroll reads it as "what the whole
  // sale is worth to this person" (see remainingCommission).
  get effectiveCommissionPercent() {
    return this.actualCommissionPercent;
  }

  get effectiveCommission() {
    return this.actualCommission;
  }

  get effectiveCommissionFormatted() {
    const commission = this.effectiveCommission;
    return commission !== null ? formatCurrency(commission) : null;
  }

  // Commission already committed to a paycheck through individual pay-app
  // approvals. On an AIA / pay-application job each pay app is approved as
  // it is invoiced, at the EXPECTED rate (`getPayAppCommission`), so this is
  // the running total of those advances.
  get approvedPayAppCommission() {
    return roundPrice(
      Object.keys(this.data.payApps).reduce(
        (sum, id) =>
          this.getPayAppStatus(id) === "unapproved"
            ? sum
            : sum + (this.getPayAppCommission(id) || 0),
        0
      )
    );
  }

  // ── Advanced Commission Payments ───────────────────────────────────
  //
  // The share of expected commission payable up front once the customer has
  // paid the full signed order total. Computed LIVE while unapproved, so
  // editing the quote before the advance is taken moves it correctly; the
  // figure is snapshotted onto the advance record at approval so a paid
  // advance can never drift.
  static get PAID_IN_FULL_SHARE() {
    return 0.5;
  }

  // The whole commission is payable (or paid): the sale is finished and its
  // actual figures exist, so there is nothing left to advance ahead of it.
  get isWholePayable() {
    return WHOLE_PAYABLE_STATUSES.includes(this.status);
  }

  // Eligible for the automatic paid-up-front advance, and none taken yet.
  // Withdrawn once the whole commission is payable: 05400082A's card showed
  // "Ready for approval" and "Advance Available $184.17" together, and the
  // half-payment was all payroll could put on the check.
  get isAdvanceAvailable() {
    return Boolean(
      this.data.paidInFullDate &&
        !this.data.advances.paidInFull &&
        !this.isWholePayable &&
        this.availableAdvance > 0
    );
  }

  // 50% of this person's own expected commission. `expectedCommission`
  // already applies their `split` through `expectedCommissionPercent`, so on
  // a team sale each member sees half of their own share.
  //
  // Capped at what has not already been advanced: a manual advance granted
  // earlier on the same sale must not let the automatic claim push the total
  // advanced past the whole expected commission.
  get availableAdvance() {
    const expected = this.expectedCommission;
    if (expected === null) return 0;
    return roundPrice(
      Math.max(
        0,
        Math.min(
          expected * Commission.PAID_IN_FULL_SHARE,
          this.remainingExpectedCommission
        )
      )
    );
  }

  // Expected commission not yet paid ahead of completion — the most payroll
  // may still advance on this sale. Expected rather than actual because an
  // advance is, by definition, taken before the actual figures exist.
  get remainingExpectedCommission() {
    const expected = this.expectedCommission;
    if (expected === null) return 0;
    return roundPrice(Math.max(0, expected - this.totalAdvanced));
  }

  get remainingExpectedCommissionFormatted() {
    return formatCurrency(this.remainingExpectedCommission);
  }

  // May payroll grant a manual advance against this sale? The sale must be
  // unfinished (ADVANCEABLE_STATUSES), its commission must not already be on
  // a check as a whole (`paycheck`) or closed out, and there must be expected
  // commission left to advance against.
  get canTakeAdvance() {
    return (
      !this.isClosed &&
      !this.data.paycheck &&
      ADVANCEABLE_STATUSES.includes(this.status) &&
      this.remainingExpectedCommission > 0
    );
  }

  get availableAdvanceFormatted() {
    return formatCurrency(this.availableAdvance);
  }

  // True when the only thing currently payable on this sale is the
  // paid-up-front advance: the customer has paid, but the job is not
  // finished so the actual figures do not exist yet.
  //
  // This is what lets an advance be claimed WITHOUT inventing a new paycheck
  // entry encoding. Ten call sites decode a paycheck's commission entries
  // with `id.split("-")`, and split-commission doc ids already collide with
  // that parse; adding another hyphen convention would make it worse. A
  // commission is instead selected the ordinary way and its state decides
  // what the entry means.
  get isClaimingAdvance() {
    // `noCommission` is accepted alongside `expected` purely to keep this
    // getter behaving exactly as it did when the two shared one status. An
    // advance is claimed before the sale completes, so `expected` is the
    // practical case; dropping `noCommission` here would quietly change which
    // sales can claim an advance rather than just relabelling a state.
    return (
      this.isAdvanceAvailable &&
      (this.status === "expected" || this.status === "noCommission")
    );
  }

  getAdvanceStatus(id) {
    const advance = this.data.advances[id];
    if (!advance) return null;
    if (!advance.paycheck) return "unapproved";
    return advance.paid ? "paid" : "approved";
  }

  // Every advance recorded against this sale, oldest first, for the card's
  // ledger: Craig wants "clear records" so a salesperson can see why a
  // true-up came out smaller than the commission they expected.
  get advanceList() {
    return Object.keys(this.data.advances || {})
      .map((id) => {
        const advance = this.data.advances[id];
        const type = ADVANCE_TYPES[advance.type]
          ? advance.type
          : ADVANCE_TYPE_PAID_IN_FULL;
        return {
          id,
          amount: advance.amount || 0,
          amountFormatted: formatCurrency(advance.amount || 0),
          date: advance.date || null,
          type,
          typeLabel: ADVANCE_TYPES[type].label,
          icon: ADVANCE_TYPES[type].icon,
          note: advance.note || "",
          paycheck: advance.paycheck || null,
          paid: Boolean(advance.paid),
          status: this.getAdvanceStatus(id),
        };
      })
      .sort((a, b) => {
        const at = a.date instanceof Date ? a.date.getTime() : Infinity;
        const bt = b.date instanceof Date ? b.date.getTime() : Infinity;
        return at - bt;
      });
  }

  // Advances already committed to a paycheck. An unapproved record is a
  // request, not money, so it does not reduce what is still owed.
  get approvedAdvances() {
    return roundPrice(
      Object.keys(this.data.advances).reduce(
        (sum, id) =>
          this.getAdvanceStatus(id) === "unapproved"
            ? sum
            : sum + (this.data.advances[id].amount || 0),
        0
      )
    );
  }

  // Everything already paid ahead of completion, from either mechanism: AIA
  // pay-app approvals and Advanced Commission Payments. One number so the
  // true-up below has a single thing to subtract.
  get totalAdvanced() {
    return roundPrice(this.approvedPayAppCommission + this.approvedAdvances);
  }

  get totalAdvancedFormatted() {
    return formatCurrency(this.totalAdvanced);
  }

  // What is still owed on the sale as a whole — the true-up at completion.
  //
  // `effectiveCommission` is everything earned on the sale, computed at the
  // ACTUAL rate. Approving pay apps pays parts of that out in advance at the
  // EXPECTED rate. Any payment path that pays a whole commission MUST use
  // this figure, not `effectiveCommission`: once every pay app is approved
  // the commission stops being "partiallyReady" and the whole record becomes
  // selectable again, so paying the gross amount pays the pay-app advances a
  // second time.
  //
  // Deliberately NOT clamped at zero: if the job landed on a worse margin
  // than expected the advances can exceed what was earned, and that
  // overpayment has to stay visible rather than being silently forgiven.
  get remainingCommission() {
    const earned = this.effectiveCommission;
    if (earned === null) {
      return null;
    }
    return roundPrice(earned - this.totalAdvanced);
  }

  // A clawback: the sale earned LESS than was already advanced, so the
  // salesperson owes the difference back. Justin: "in rare cases a major
  // problem leads to charges or costs that reduce the sale to below the
  // profit to pay. In this case we need to claw back the commission."
  //
  // Positive number = dollars owed back. Zero when nothing is owed, so it
  // reads naturally in the UI without callers handling signs.
  get owedBack() {
    // A clawback recovers an ADVANCE, so it cannot exist when nothing was
    // advanced and cannot exceed what was. Without both bounds a negative
    // remainder that was never paid out showed a phantom "Owed Back" (before
    // splits were capped at what the sale earns, a below-minimum-margin sale
    // with a split given did exactly that).
    const advanced = this.totalAdvanced;
    if (!(advanced > 0)) return 0;
    const remaining = this.remainingCommission;
    if (remaining === null || remaining >= 0) return 0;
    return Math.min(roundPrice(-remaining), advanced);
  }

  get owedBackFormatted() {
    return formatCurrency(this.owedBack);
  }

  get hasClawback() {
    return this.owedBack > 0;
  }

  // What a true-up paycheck actually DEDUCTS for this sale: the full
  // negative remainder, not just the advance-attributable part. `owedBack`
  // bounds the clawback at what was advanced, but Paycheck.commissionTotal
  // nets the whole remainder, so the card must show THIS figure or it
  // understates the check. The two only differ when the sale itself earned
  // a negative amount (a refund taking the actual pre-tax below zero);
  // splits given are capped at what the sale earns and cannot do it.
  get netDeduction() {
    const remaining = this.remainingCommission;
    if (remaining === null || remaining >= 0) return 0;
    return roundPrice(-remaining);
  }

  get netDeductionFormatted() {
    return formatCurrency(this.netDeduction);
  }

  get remainingCommissionFormatted() {
    const commission = this.remainingCommission;
    return commission !== null ? formatCurrency(commission) : null;
  }

  // True when pay-app advances have already been approved against this sale,
  // i.e. the whole-commission payout is a true-up rather than the full amount.
  get hasPayAppAdvances() {
    return this.approvedPayAppCommission !== 0;
  }

  // Any advance at all has been paid against this sale, from either
  // mechanism — what the card's "Already Advanced" row keys off.
  get hasAdvances() {
    return this.totalAdvanced !== 0;
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }
}
